import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { safeJsonParse, safeLocalStorageSet } from '../utils/storage';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const buildSelectedMembershipUser = ({
  currentUser,
  nextTenant,
  membership,
  effectivePermissions,
}) => ({
  ...currentUser,
  tenant_id: nextTenant?.id,
  membership,
  account_kind: membership?.membership_kind || currentUser?.account_kind,
  permissions: effectivePermissions
    && typeof effectivePermissions === 'object'
    && !Array.isArray(effectivePermissions)
    ? effectivePermissions
    : {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const authenticatedUserId = user?.id;
  const activeTenantSlug = tenant?.slug;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const rawUser = localStorage.getItem('user');
    const rawTenant = localStorage.getItem('tenant');
    const rawMemberships = localStorage.getItem('memberships');

    // Auto-clean corrupted 'undefined' or 'null' literals from localStorage
    if (rawUser === 'undefined' || rawUser === 'null') localStorage.removeItem('user');
    if (rawTenant === 'undefined' || rawTenant === 'null') localStorage.removeItem('tenant');
    if (rawMemberships === 'undefined' || rawMemberships === 'null') localStorage.removeItem('memberships');

    const savedUser = safeJsonParse(rawUser);
    const savedTenant = safeJsonParse(rawTenant);
    const savedMemberships = safeJsonParse(rawMemberships, []);

    if (token && savedUser) {
      setUser(savedUser);
      if (savedTenant) setTenant(savedTenant);
      if (savedMemberships) setMemberships(savedMemberships);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authenticatedUserId || !localStorage.getItem('token')) return;
    let active = true;
    authAPI.getMemberships().then(response => {
      if (!active) return;
      const rows = response.data.memberships || [];
      setMemberships(rows);
      localStorage.setItem('memberships', JSON.stringify(rows));
    }).catch(() => {});

    // Refresh server-calculated permissions as well as memberships. This keeps
    // long-lived browser sessions current after a reviewed RBAC migration and
    // avoids trusting stale or empty membership capabilities from localStorage.
    if (activeTenantSlug) {
      authAPI.selectMembership(activeTenantSlug).then(response => {
        if (!active) return;
        const {
          tenant: nextTenant,
          membership,
          effective_permissions: effectivePermissions,
        } = response.data;
        setTenant(nextTenant);
        setUser(currentUser => {
          const nextUser = buildSelectedMembershipUser({
            currentUser,
            nextTenant,
            membership,
            effectivePermissions,
          });
          safeLocalStorageSet('user', nextUser);
          return nextUser;
        });
        safeLocalStorageSet('tenant', nextTenant);
        localStorage.setItem('portalType', membership?.portal_type || 'citizen');
      }).catch(() => {});
    }
    return () => { active = false; };
  }, [authenticatedUserId, activeTenantSlug]);

  const login = useCallback(async (email, password, tenant_slug, portal = 'gp') => {
    const res = await authAPI.login({ email, password, tenant_slug, portal });
    const { token, user: userData, tenant: tenantData, portalType, memberships: availableMemberships = [] } = res.data;

    localStorage.setItem('token', token);
    safeLocalStorageSet('user', userData);
    localStorage.setItem('portalType', portalType || 'admin');
    safeLocalStorageSet('tenant', tenantData);
    safeLocalStorageSet('memberships', availableMemberships);

    setUser(userData);
    setTenant(tenantData);
    setMemberships(availableMemberships);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authAPI.register(data);
    // Registration now returns pending status, no auto-login
    return res.data;
  }, []);

  const requestOtp = useCallback(async (phone, tenant_slug, portal = 'gp') => {
    const res = await authAPI.requestOtp({ phone, tenant_slug, portal });
    return res.data;
  }, []);

  const loginWithOtp = useCallback(async (phone, otp, tenant_slug, portal = 'gp') => {
    const res = await authAPI.verifyOtp({ phone, otp, tenant_slug, portal });
    const { token, user: userData, tenant: tenantData, portalType, memberships: availableMemberships = [] } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('portalType', portalType || 'admin');
    if (tenantData) localStorage.setItem('tenant', JSON.stringify(tenantData));
    localStorage.setItem('memberships', JSON.stringify(availableMemberships));
    setUser(userData);
    setTenant(tenantData);
    setMemberships(availableMemberships);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('portalType');
    localStorage.removeItem('memberships');
    setUser(null);
    setTenant(null);
    setMemberships([]);
  }, []);

  const refreshMemberships = useCallback(async () => {
    const response = await authAPI.getMemberships();
    const rows = response.data.memberships || [];
    setMemberships(rows);
    localStorage.setItem('memberships', JSON.stringify(rows));
    return rows;
  }, []);

  const selectMembership = useCallback(async tenantSlug => {
    const response = await authAPI.selectMembership(tenantSlug);
    const {
      tenant: nextTenant,
      membership,
      effective_permissions: effectivePermissions,
    } = response.data;
    const nextUser = buildSelectedMembershipUser({
      currentUser: user,
      nextTenant,
      membership,
      effectivePermissions,
    });
    setTenant(nextTenant);
    setUser(nextUser);
    safeLocalStorageSet('tenant', nextTenant);
    safeLocalStorageSet('user', nextUser);
    localStorage.setItem('portalType', membership?.portal_type || 'citizen');
    return {
      tenant: nextTenant,
      membership,
      effective_permissions: effectivePermissions,
    };
  }, [user]);

  const value = {
    user,
    tenant,
    memberships,
    loading,
    isAuthenticated: !!user,
    login,
    loginWithOtp,
    requestOtp,
    register,
    logout,
    refreshMemberships,
    selectMembership,
    setUser,
    setTenant,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
